import type { RulebookProfile } from "@next-platform/client-common-core";
import { Button, ButtonIcon, ButtonVariant, IconVariants, Shape, Tooltip } from "@next-platform/client-common-spicy-ui";
import type { TFunction } from "i18next";

import { BlockCreatorModal, CapabilityShareRulebookModal, Row } from "@/components";
import RulebookAttachTargetModal from "@/components/Rulebook/RulebookAttachTargetModal";
import Trans from "@/components/Trans/Trans";

export type RulebookProfileActionsProps = {
  profile: RulebookProfile;
  isShareable: boolean;
  isCloning: boolean;
  isOpening: boolean;
  canAttach: boolean;
  onAttach: () => void;
  isDraftOwnerReady: boolean;
  isManageAllowed: boolean;
  onShare: () => void;
  onClone: () => void;
  onExport: () => void;
  onEdit: () => void;
  onReport: () => void;
  onBlock: () => void;
  t: TFunction;
};

/** Action row: Attach, Clone/Export, Edit (owner), then icon-only Share/Report/Block. */
type RulebookProfilePrimaryActionsProps = Pick<
  RulebookProfileActionsProps,
  "profile" | "canAttach" | "onAttach" | "isManageAllowed" | "isCloning" | "isDraftOwnerReady" | "isOpening" | "onClone" | "onExport" | "onEdit" | "t"
>;

const RulebookProfilePrimaryActions = ({
  profile,
  canAttach,
  onAttach,
  isManageAllowed,
  isCloning,
  isDraftOwnerReady,
  isOpening,
  onClone,
  onExport,
  onEdit,
  t,
}: RulebookProfilePrimaryActionsProps) => {
  const isPrivateDefinition = !profile.definitionVisible;
  const cloneTooltip = isPrivateDefinition ? t("rulebook:profile.definitionPrivateHint") : t("rulebook:profile.cloneHint");
  const exportTooltip = isPrivateDefinition ? t("rulebook:profile.definitionPrivateHint") : t("rulebook:profile.exportHint");
  return (
    <>
      {canAttach ? (
        <Button iconStart={IconVariants.PLUS} variant={ButtonVariant.CONTAINED} onClick={onAttach} data-testid="RulebookProfile-AttachButton">
          <Trans i18nKey="rulebook:card.actions.attach" />
        </Button>
      ) : null}
      {!profile.isOwner && isManageAllowed ? (
        <Tooltip title={cloneTooltip}>
          <span className="inline-flex" data-testid="RulebookProfile-CloneTooltip">
            <Button
              iconStart={IconVariants.COPY}
              variant={ButtonVariant.OUTLINED}
              onClick={onClone}
              disabled={isPrivateDefinition || isCloning || !isDraftOwnerReady}
              loading={isCloning}
              data-testid="RulebookProfile-CloneButton"
            >
              <Trans i18nKey="rulebook:explore.clone" />
            </Button>
          </span>
        </Tooltip>
      ) : null}
      {!profile.isOwner && isManageAllowed ? (
        <Tooltip title={exportTooltip}>
          <span className="inline-flex" data-testid="RulebookProfile-ExportTooltip">
            <Button
              iconStart={IconVariants.DOWNLOAD}
              variant={ButtonVariant.OUTLINED}
              onClick={onExport}
              disabled={isPrivateDefinition || !profile.definition}
              data-testid="RulebookProfile-Export"
            >
              <Trans i18nKey="rulebook:profile.export" />
            </Button>
          </span>
        </Tooltip>
      ) : null}
      {profile.isOwner && isManageAllowed ? (
        <Button
          iconStart={IconVariants.EDIT}
          variant={ButtonVariant.CONTAINED}
          onClick={onEdit}
          disabled={isOpening || !isDraftOwnerReady}
          data-testid="RulebookProfile-EditButton"
        >
          <Trans i18nKey="rulebook:profile.edit" />
        </Button>
      ) : null}
    </>
  );
};

type RulebookProfileSocialActionsProps = Pick<RulebookProfileActionsProps, "profile" | "isShareable" | "onShare" | "onReport" | "onBlock" | "t">;

const RulebookProfileSocialActions = ({ profile, isShareable, onShare, onReport, onBlock, t }: RulebookProfileSocialActionsProps) => (
  <>
    {isShareable ? (
      <Tooltip title={t("common:share")}>
        <ButtonIcon
          icon={IconVariants.SHARE}
          variant={ButtonVariant.OUTLINED}
          shape={Shape.SQUARE}
          onClick={onShare}
          aria-label={t("common:share")}
          data-testid="RulebookProfile-ShareButton"
        />
      </Tooltip>
    ) : null}
    {!profile.isOwner ? (
      <>
        <Tooltip title={t("rulebook:profile.report")}>
          <ButtonIcon
            icon={IconVariants.FLAG}
            variant={ButtonVariant.OUTLINED}
            shape={Shape.SQUARE}
            onClick={onReport}
            aria-label={t("rulebook:profile.report")}
            data-testid="RulebookProfile-ReportButton"
          />
        </Tooltip>
        <Tooltip title={t("rulebook:profile.blockCreator")}>
          <ButtonIcon
            icon={IconVariants.BLOCKS}
            variant={ButtonVariant.OUTLINED}
            shape={Shape.SQUARE}
            onClick={onBlock}
            aria-label={t("rulebook:profile.blockCreator")}
            data-testid="RulebookProfile-BlockButton"
          />
        </Tooltip>
      </>
    ) : null}
  </>
);

const RulebookProfileActions = (actionsProps: RulebookProfileActionsProps) => (
  <Row className="flex-wrap items-center gap-2" data-testid="RulebookProfile-Actions">
    <RulebookProfilePrimaryActions
      profile={actionsProps.profile}
      canAttach={actionsProps.canAttach}
      onAttach={actionsProps.onAttach}
      isManageAllowed={actionsProps.isManageAllowed}
      isCloning={actionsProps.isCloning}
      isDraftOwnerReady={actionsProps.isDraftOwnerReady}
      isOpening={actionsProps.isOpening}
      onClone={actionsProps.onClone}
      onExport={actionsProps.onExport}
      onEdit={actionsProps.onEdit}
      t={actionsProps.t}
    />
    <RulebookProfileSocialActions
      profile={actionsProps.profile}
      isShareable={actionsProps.isShareable}
      onShare={actionsProps.onShare}
      onReport={actionsProps.onReport}
      onBlock={actionsProps.onBlock}
      t={actionsProps.t}
    />
  </Row>
);

export type RulebookProfileModalsProps = {
  profile: RulebookProfile;
  isShareable: boolean;
  isShareVisible: boolean;
  isBlockVisible: boolean;
  isAttachVisible: boolean;
  onCloseAttach: () => void;
  onRulebookAttached: () => void;
  shareUrl: string;
  setShowShare: (show: boolean) => void;
  onCloseBlock: () => void;
};

export const RulebookProfileModals = ({
  profile,
  isShareable,
  isShareVisible,
  isBlockVisible,
  isAttachVisible,
  shareUrl,
  setShowShare,
  onCloseBlock,
  onCloseAttach,
  onRulebookAttached,
}: RulebookProfileModalsProps) => (
  <>
    {isShareable ? (
      <CapabilityShareRulebookModal
        visible={isShareVisible}
        setShowShare={setShowShare}
        shareUrl={shareUrl}
        characterId={profile.id}
        characterName={profile.name}
      />
    ) : null}
    {isBlockVisible ? (
      <BlockCreatorModal
        creatorId={profile.creatorUserId}
        creatorUsername={profile.creatorUsername ?? ""}
        isVisible={isBlockVisible}
        onClose={onCloseBlock}
      />
    ) : null}
    {isAttachVisible ? (
      <RulebookAttachTargetModal isOpen onClose={onCloseAttach} rulebookId={profile.id} rulebookName={profile.name} onAttached={onRulebookAttached} />
    ) : null}
  </>
);

export default RulebookProfileActions;
